/**
 * Live Mode
 * Handles microphone input for live audio visualization
 */

import { audioState, playbackState, uiState, exportState, managers } from './state.js';
import { elements } from './dom-cache.js';
import { stopPlayback, isPlaybackBlocked, ensureVisualizerReady } from './playback.js';
import { createFeatureExtractor } from '../lib/audio-features.js';
import { updateLiveModeButton } from './overlay-panels.js';
import { closeOverlay as closePlaylistOverlay } from './playlist-overlay.js';
import { showNotification } from '../lib/notification-utils.js';

/**
 * Enter live mode with microphone input
 */
export async function enterLiveMode() {
  try {
    // Exit if already in live mode
    if (uiState.isLiveMode) return;
    // Prevent entering live mode during export
    if (isPlaybackBlocked()) return;

    // Ensure a visualizer is ready before starting live mode (lazy initialization)
    await ensureVisualizerReady();

    // Stop any current playback
    stopPlayback();

    // Collapse the playlist overlay if it's open
    closePlaylistOverlay();

    // Request microphone access
    uiState.liveStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false
      }
    });

    // Create audio context if needed
    if (!audioState.audioContext) {
      audioState.audioContext = new AudioContext();
    } else if (audioState.audioContext.state === 'suspended') {
      await audioState.audioContext.resume();
    }

    // Create source from microphone stream
    uiState.liveSourceNode = audioState.audioContext.createMediaStreamSource(uiState.liveStream);

    // Create analyser for visualization
    audioState.analyserNode = audioState.audioContext.createAnalyser();
    audioState.analyserNode.fftSize = 2048;
    audioState.analyserNode.smoothingTimeConstant = 0;

    // Connect: microphone -> analyser
    uiState.liveSourceNode.connect(audioState.analyserNode);

    // Connect to destination if monitoring is enabled
    if (uiState.isLiveMonitoring) {
      audioState.analyserNode.connect(audioState.audioContext.destination);
    }

    // Create or reset feature extractor for live mode
    if (!audioState.featureExtractor) {
      audioState.featureExtractor = createFeatureExtractor({
        sampleRate: audioState.audioContext.sampleRate,
        fftSize: audioState.analyserNode.fftSize
      });
    } else {
      audioState.featureExtractor.reset();
    }

    uiState.isLiveMode = true;

    // Update UI
    updateLiveModeUI(true);

    // Dispatch event so overlay-panels can hide dropzone
    document.dispatchEvent(new CustomEvent('liveModeEntered'));

    // Start visualization loop
    animateLive();

  } catch (error) {
    console.error('Microphone access error:', error);
    showNotification('Could not access microphone: ' + error.message, { type: 'error' });
  }
}

/**
 * Exit live mode and cleanup resources
 */
export function exitLiveMode() {
  if (!uiState.isLiveMode) return;

  uiState.isLiveMode = false;

  // Disconnect and cleanup
  if (uiState.liveSourceNode) {
    uiState.liveSourceNode.disconnect();
    uiState.liveSourceNode = null;
  }
  if (audioState.analyserNode) {
    try {
      audioState.analyserNode.disconnect();
    } catch (e) {}
    audioState.analyserNode = null;
  }
  if (uiState.liveStream) {
    uiState.liveStream.getTracks().forEach(track => track.stop());
    uiState.liveStream = null;
  }

  // Cancel animation
  if (playbackState.animationId) {
    cancelAnimationFrame(playbackState.animationId);
    playbackState.animationId = null;
  }

  // Update UI
  updateLiveModeUI(false);
  managers.visualizerManager.clear();

  // Dispatch event so overlay-panels can show dropzone if needed
  document.dispatchEvent(new CustomEvent('liveModeExited'));
}

/**
 * Toggle live mode on/off
 */
export function toggleLiveMode() {
  if (uiState.isLiveMode) {
    exitLiveMode();
  } else {
    enterLiveMode();
  }
}

/**
 * Toggle live monitoring (output to speakers)
 */
export function toggleLiveMonitoring(enabled) {
  uiState.isLiveMonitoring = enabled;

  // If live mode is active, update the audio routing
  if (uiState.isLiveMode && audioState.analyserNode && audioState.audioContext) {
    try {
      if (enabled) {
        audioState.analyserNode.connect(audioState.audioContext.destination);
      } else {
        audioState.analyserNode.disconnect(audioState.audioContext.destination);
      }
    } catch (e) {
      // Ignore disconnect errors if not connected
    }
  }
}

/**
 * Animation loop for live mode visualization
 */
export function animateLive() {
  if (!uiState.isLiveMode || !audioState.analyserNode) return;

  // Get frequency data (reuse buffer to avoid per-frame allocations)
  if (!audioState.frequencyDataBuffer || audioState.frequencyDataBuffer.length !== audioState.analyserNode.frequencyBinCount) {
    audioState.frequencyDataBuffer = new Uint8Array(audioState.analyserNode.frequencyBinCount);
  }
  audioState.analyserNode.getByteFrequencyData(audioState.frequencyDataBuffer);

  // Get time-domain data for CQT support (reuse buffer)
  if (!audioState.timeDomainBuffer || audioState.timeDomainBuffer.length !== audioState.analyserNode.fftSize) {
    audioState.timeDomainBuffer = new Float32Array(audioState.analyserNode.fftSize);
  }
  audioState.analyserNode.getFloatTimeDomainData(audioState.timeDomainBuffer);

  // Extract enhanced audio features (lazy evaluation - only computed if visualizer accesses them)
  const features = audioState.featureExtractor
    ? audioState.featureExtractor.extract(audioState.frequencyDataBuffer, audioState.timeDomainBuffer)
    : null;

  // Draw visualization with optional features (wrapped in try-catch for error resilience)
  try {
    managers.visualizerManager.draw(audioState.frequencyDataBuffer, features);
  } catch (error) {
    console.error('[LiveMode] Visualizer draw error:', error);
    // Continue animation loop despite draw errors
  }

  // No lyrics in live mode, but clear overlay
  if (elements.lyricsOverlayCtx && elements.lyricsOverlayCanvas) {
    elements.lyricsOverlayCtx.clearRect(0, 0, elements.lyricsOverlayCanvas.width, elements.lyricsOverlayCanvas.height);
  }

  // Record frame for performance monitoring
  managers.perfMonitor.recordFrame();

  playbackState.animationId = requestAnimationFrame(animateLive);
}

/**
 * Update UI elements for live mode state
 */
export function updateLiveModeUI(active) {
  // Update body class for global state tracking
  document.body.classList.toggle('live-mode-active', active);

  // Update icon sidebar button
  updateLiveModeButton(active);

  // Update legacy button if present
  if (elements.liveModeBtn) {
    if (active) {
      elements.liveModeBtn.classList.add('live-active');
      elements.liveModeBtn.innerHTML = '&#x1F534; Live';
    } else {
      elements.liveModeBtn.classList.remove('live-active');
      elements.liveModeBtn.innerHTML = '&#x1F3A4; Live';
    }
  }

  // Show/hide monitoring option
  if (elements.liveMonitorLabel) {
    elements.liveMonitorLabel.style.display = active ? 'inline-flex' : 'none';
  }

  // In live mode: keep transport buttons enabled but hide seek bar area
  // and show LIVE indicator instead
  if (elements.seekBar) elements.seekBar.disabled = active;

  // Toggle LIVE indicator visibility
  updateLiveIndicator(active);
}

/**
 * Create or update the LIVE indicator in transport bar
 */
function updateLiveIndicator(active) {
  const transportControls = document.querySelector('.transport-controls');
  if (!transportControls) return;

  // Find or create live indicator container
  let liveIndicator = transportControls.querySelector('.live-indicator');

  if (active) {
    // Hide seek bar and time elements
    if (elements.seekBar) elements.seekBar.style.display = 'none';
    if (elements.currentTimeEl) elements.currentTimeEl.style.display = 'none';
    if (elements.durationEl) elements.durationEl.style.display = 'none';

    // Create live indicator if not exists
    if (!liveIndicator) {
      liveIndicator = document.createElement('div');
      liveIndicator.className = 'live-indicator';
      liveIndicator.innerHTML = `
        <i data-lucide="mic" class="live-indicator-icon"></i>
        <span class="live-indicator-text">LIVE</span>
        <div class="live-indicator-wave">
          <span class="wave-bar"></span>
          <span class="wave-bar"></span>
          <span class="wave-bar"></span>
          <span class="wave-bar"></span>
          <span class="wave-bar"></span>
        </div>
      `;

      // Insert after duration (or at end of controls)
      const durationEl = transportControls.querySelector('#duration');
      if (durationEl) {
        durationEl.parentNode.insertBefore(liveIndicator, durationEl.nextSibling);
      } else {
        transportControls.appendChild(liveIndicator);
      }

      // Create icons
      if (window.lucide) {
        window.lucide.createIcons({ nodes: [liveIndicator] });
      }
    }

    liveIndicator.style.display = 'flex';
  } else {
    // Show seek bar and time elements
    if (elements.seekBar) elements.seekBar.style.display = '';
    if (elements.currentTimeEl) elements.currentTimeEl.style.display = '';
    if (elements.durationEl) elements.durationEl.style.display = '';

    // Hide live indicator
    if (liveIndicator) {
      liveIndicator.style.display = 'none';
    }
  }
}

/**
 * Set up live mode event listeners
 */
export function setupLiveModeListeners() {
  // Legacy button handler
  if (elements.liveModeBtn) {
    elements.liveModeBtn.addEventListener('click', toggleLiveMode);
  }

  // Monitoring checkbox handler
  if (elements.liveMonitorCheckbox) {
    elements.liveMonitorCheckbox.addEventListener('change', (e) => {
      toggleLiveMonitoring(e.target.checked);
    });
  }

  // Listen for custom event from icon sidebar
  document.addEventListener('toggleLiveMode', toggleLiveMode);
}
